import { useMemo } from 'react';

import { ensureArray } from '@/utils/pagination';

import {
  FilterDefinition,
  TASK_FILTERS_CONFIG,
  TaskFilterParams,
} from '../config/filterConfig';

/**
 * Hook to map frontend filter state (URL params) to backend Service/API parameters.
 * Maps frontend filter state (URL params) to backend Service/API parameters.
 *
 * NOW GENERATED DYNAMICALLY from filterConfig.ts
 *
 * @param queryParams - The raw parameters from the URL/State
 * @returns A generic object that matches the TaskFilter interface expected by the Service
 */
export const useTaskFilterMapper = (queryParams: Partial<TaskFilterParams>) => {
  return useMemo(() => {
    const apiParams: Record<
      string,
      string | number | string[] | boolean | null | undefined
    > = {};

    // Iterate over the centrally defined configuration
    // This acts as an allowlist (security) and a mapper (logic)
    (
      Object.keys(TASK_FILTERS_CONFIG) as Array<
        keyof typeof TASK_FILTERS_CONFIG
      >
    ).forEach((configKey) => {
      const config = TASK_FILTERS_CONFIG[configKey] as FilterDefinition;

      // Determine the input key (URL key)
      const rawValue = queryParams[configKey];

      // 1. Handle Defaults
      if (rawValue === undefined || rawValue === null) {
        if (config.defaultValue !== undefined) {
          apiParams[config.serviceKey] = config.defaultValue;
        }
        return;
      }

      // 2. Apply Transformation Logic
      if (config.isArray) {
        // We cast to string | string[] because isArray=true implies this per our types,
        // and ensureArray expects this. Numbers (limit/offset) have isArray=false.
        apiParams[config.serviceKey] = ensureArray(
          rawValue as string | string[],
        );
      } else {
        apiParams[config.serviceKey] = rawValue;
      }
    });

    // 3. Escape Hatch / Special Logic
    // If there are complex conditional filters that cannot be expressed in the static config,
    // handle them here.
    // Example:
    // if (queryParams.someSpecialFlag) { ... }

    return apiParams;
  }, [queryParams]);
};
